import { Box, Button, ButtonBase, Checkbox, Menu, MenuItem, OutlinedInput, InputAdornment, ListItemText, styled, Typography, useTheme } from "@mui/material";
import Stack from '@mui/material/Stack';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import { bgBlur } from 'src/theme/css';
import { useResponsive } from 'src/hooks/use-responsive';
import Logo from 'src/components/logo';
import SvgColor from 'src/components/svg-color';
import { useSettingsContext } from 'src/components/settings';
import { useDateRangeSelect } from 'src/components/generic-select/date-range-select';
import { useEffect, useMemo, useRef, useState } from 'react';
import Iconify from 'src/components/iconify';
import DateRangeSelect from 'src/components/generic-select/date-range-select';
import { fDate, getDateRangeIso } from 'src/utils/format-time';
import { useDispatch, useSelector } from "react-redux";
import InfiniteScrollKeywords from "src/sections/overview/dashboards/views/InfiniteScrollKeywords";
import Autocomplete from '@mui/material/Autocomplete';
import Chip from '@mui/material/Chip';
import TextField from '@mui/material/TextField';
import { FlagIcon } from "src/components/iconify/flagIcon";
import { subDays, differenceInDays } from "date-fns";
import { useCurrentWorkspace } from "src/context/reducers/app-settings";
import { useLocales } from "src/locales";
import { useCurrentUserQuery } from "src/context/api/auth";
import { RootState } from "src/context/store";
import { useSearchParams } from "react-router-dom";
import { useRouter, usePathname } from "src/routes/hooks";
import { NavbarProps, AdType } from "src/components/competitive-intelligence/types";
import { setAdType as setAdTypeAction, setSearchQuery as setSearchQueryAction, setSortBy as setSortByAction, setSortDirection as setSortDirectionAction } from 'src/context/reducers/competitive-intelligence/adsSlice';
import { setFilterDate, setFilterDevice, setFilterCountry, setFilterKeyword, setFilterTimestep, setFilterDateCompare, setFilterPlatform, setFilterMinDatePicker, setExtraOptions, setSavingsFormulaFilter, setFilterLoadedDevice, setFilterLoadedPlatform, setFilterLoadedCountry, setFilterLoadedKeyword, setFilterTags, setFilterLoadedTags } from '../../context/reducers/filter-settings/filterSlice';
import { AccountPopover, NotificationsPopover } from '../_common';
import { HEADER, NAV } from '../config-layout';
import countriesJson from "../../utils/countries.json"
import FiltersButton from "../_common/filters-button";
import SettingsPanel from "../_common/settings/settings-panel";
import { getTabs } from "./header-tabs";
import { useCountriesQuery, usePlatformsQuery, useSearchTermsQuery } from "src/context/api/filters/api";
import GenericInfiniteScrollList from "src/sections/overview/dashboards/views/generic-infinite-scroll-list";
import { useGetSearchTermsByTagsQuery, useGetTagsListQuery } from "src/context/api";
import { generateColorFromString } from "src/sections/overview/dashboards/views/tag-manager/datagrid/add-tag-cell";

type Props = {
  onOpenNav?: VoidFunction;
};

const TabButton = styled(ButtonBase)(({ theme }: any) => ({
  padding: theme.spacing(0.5, 1.5),
  borderRadius: 6,
  fontSize: 15,
  fontWeight: 400,
  color: theme.palette.text.secondary,
  "&.active": {
    fontWeight: 600,
    color: "#333C47",
    backgroundColor: "#f1f7b5",
  },
  "&:not(.active):hover": {
    color: theme.palette.mode === "dark"
      ? theme.palette.common.white
      : "black",
    backgroundColor:
      theme.palette.mode === "dark"
        ? "rgba(255, 255, 255, 0.05)"
        : "rgba(145, 158, 171, 0.1)",
  },
}));
// Moved out of Header to comply with react/no-unstable-nested-components
const KoBiddingDropdown = ({ router, location }: any) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const options = [
    { label: "Max CPC", path: "/optimization/activations/ko-bidding/max-cpc" },
    { label: "Target ROAS / Maximize Conversion Value", path: "/optimization/activations/ko-bidding/troas" },
    { label: "Target CPA / Maximize Conversion", path: "/optimization/activations/ko-bidding/tcpa" },
    { label: "Target Impression Share", path: "/optimization/activations/ko-bidding/target-impression-share" },
    { label: "Shopping", path: "/admin/features/live-bidding/ad-group-based" },
  ];

  const current = options.find((opt) => location.pathname === opt.path) || options[0];

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (path?: string) => {
    setAnchorEl(null);
    if (path) router.push(path);
  };

  return (
    <>
      <TabButton
        onClick={handleClick}
        className={
          location.pathname.startsWith("/optimization/activations/ko-bidding/")
            ? "active"
            : ""
        }
        sx={{ mr: 1, display: "flex", alignItems: "center", gap: 0.5 }}
      >
        KO Bidding Manual: {current.label}
        <Iconify
          icon={open ? "eva:arrow-ios-upward-fill" : "eva:arrow-ios-downward-fill"}
          width={18}
          height={18}
        />
      </TabButton>

      <Menu anchorEl={anchorEl} open={open} onClose={() => handleClose()}>
        {options.map((opt) => (
          <MenuItem
            key={opt.path}
            onClick={() => handleClose(opt.path)}
            selected={location.pathname === opt.path}
          >
            {opt.label}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

// Competitive Intelligence Ads Header - moved out of Header to comply with lint rule
type CIAHeaderProps = Partial<NavbarProps>;

const CompetitiveIntelligenceAdsHeader = ({
  adType,
  counts = { ALL: 0, PLA: 0, TEXT: 0, RightPanel: 0 },
  searchQuery,
  sortBy,
  sortDirection,
}: CIAHeaderProps) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const ui = useSelector((state: any) => state.competitiveIntelligenceAdsUi || { adType: 'ALL', searchQuery: '', sortBy: 'Freq.', sortDirection: 'desc' });
  const effectiveAdType = adType ?? ui.adType;
  const effectiveSearchQuery = searchQuery ?? ui.searchQuery;
  const effectiveSortBy = sortBy ?? ui.sortBy;
  const effectiveSortDirection = sortDirection ?? ui.sortDirection;
  const [typeAnchorEl, setTypeAnchorEl] = useState<null | HTMLElement>(null);
  const typeOpen = Boolean(typeAnchorEl);
  const openTypeMenu = (event: any) => setTypeAnchorEl(event.currentTarget);
  const closeTypeMenu = () => setTypeAnchorEl(null);

  // Dropdown for sorting
  const [sortAnchorEl, setSortAnchorEl] = useState<null | HTMLElement>(null);
  const sortOpen = Boolean(sortAnchorEl);
  const openSortMenu = (event: any) => setSortAnchorEl(event.currentTarget);
  const closeSortMenu = () => setSortAnchorEl(null);

  const items = useMemo(() => ([
    { key: 'ALL' as AdType, label: 'All ads' },
    { key: 'PLA' as AdType, label: 'PLA' },
    { key: 'TEXT' as AdType, label: 'Text ads' },
  ]), [counts.ALL, counts.PLA, counts.TEXT]);

  const selected: any = items.find((i) => i.key === effectiveAdType);

  const handleAdTypeChange = (newValue: any) => {
    const key = newValue?.key || 'ALL';
    dispatch(setAdTypeAction(key));
  };

  return (
    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ width: '100%' }}>
      {/* Left side: Ads type + filter icon */}
      <Stack direction="row" alignItems="center" spacing={1}>

        <Autocomplete
          disablePortal
          options={items}
          value={selected || null}
          onChange={(event, newValue) => handleAdTypeChange(newValue)}
          getOptionLabel={(option) => option.label}
          size="small"
          disableClearable
          popupIcon={<Iconify icon="mdi:chevron-down" fontSize="small" />}
          sx={{ width: 130 }}
          renderInput={(params) => (
            <TextField
              {...params}
              InputProps={{
                ...params.InputProps,
                startAdornment: selected ? (
                  <Chip
                    label={selected.label}
                    size="small"
                    sx={{
                      backgroundColor: 'rgba(145, 158, 171, 0.12)',
                      color: 'rgba(51, 60, 71, 1)',
                      mr: 1,
                    }}
                  />
                ) : null,
                // on retire le texte par défaut
                inputProps: {
                  ...params.inputProps,
                  style: { display: 'none' },
                },
              }}
            />
          )}
        />

        <IconButton
          onClick={() => {
            // Delegate to the active page to open its own filters drawer
            window.dispatchEvent(new CustomEvent('openPageFiltersDrawer'))
          }}
          sx={{
            width: 32,
            height: 32,
            borderRadius: 1,
            bgcolor: "#f2f3f5",
            color: "#919EAB",
            p: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            '&:hover': {
              bgcolor: theme.palette.mode === 'dark' ? '#3F4752' : '#E0E0E0',
              borderColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.4)',
            },
          }}
        >
          <Iconify icon="majesticons:filter-line" width={16} height={16} />
        </IconButton>
      </Stack>

      {/* Right side: Search + Sort by */}
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ minWidth: 0 }}>
        <OutlinedInput
          size="small"
          placeholder="Search"
          value={effectiveSearchQuery}
          onChange={(e) => dispatch(setSearchQueryAction(e.target.value))}
          startAdornment={<InputAdornment position="start"><Iconify icon="mdi:magnify" fontSize="small" /></InputAdornment>}
          sx={{
            width: '200px',
            bgcolor: 'transparent',
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: "rgba(145, 158, 171, 0.20)",
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: "rgba(145, 158, 171, 0.20)",
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: "rgba(145, 158, 171, 0.20)",
            },
            '& input': {
              color: "rgba(99, 115, 129, 1)",
            },
            '& input::placeholder': {
              color: "rgba(99, 115, 129, 1)",
            }
          }}
        />
        <Box
          sx={{
            position: 'relative',
            border: `1px solid rgba(145, 158, 171, 0.20)`,
            borderRadius: 1,
            width: 130,
            px: 1,
            pt: 1.5,
            pb: 1,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Button
            size="small"
            variant="text"
            color="inherit"
            onClick={openSortMenu}
            endIcon={<Iconify icon="mdi:chevron-down" fontSize="small" />}
            startIcon={<Iconify icon={effectiveSortDirection === 'desc' ? 'gravity-ui:bars-descending-align-left' : 'gravity-ui:bars-ascending-align-left'} fontSize="small" />}
            sx={{
              color: "rgba(51, 60, 71, 1)",
              textTransform: 'none',
              borderRadius: 1,
              px: 0.5,
              height: 14,
              minWidth: 10,
              justifyContent: 'space-between',
              '& .MuiButton-startIcon': { mr: 1 },
              '& .MuiButton-endIcon': { ml: 1 },
              fontWeight: 600,
            }}
          >
            {effectiveSortBy}
          </Button>
        </Box>
      </Stack>

      {/* Type menu */}
      <Menu
        anchorEl={typeAnchorEl}
        open={typeOpen}
        onClose={closeTypeMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        MenuListProps={{ sx: { textAlign: 'right', minWidth: 90, maxWidth: 90 } }}
        slotProps={{ paper: { sx: { mt: 1 } } }}
      >
        {items.map((item) => (
          <MenuItem
            key={item.key}
            selected={item.key === effectiveAdType}
            onClick={() => {
              dispatch(setAdTypeAction(item.key));
              closeTypeMenu();
            }}
          >
            <ListItemText primary={item.label} />
          </MenuItem>
        ))}
      </Menu>

      {/* Sort menu */}
      <Menu
        anchorEl={sortAnchorEl}
        open={sortOpen}
        onClose={closeSortMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        slotProps={{ paper: { sx: { mt: 2, minWidth: 135 } } }}
      >
        {['Freq.', 'Position', 'Price'].map((opt) => (
          <MenuItem
            key={opt}
            selected={opt === effectiveSortBy}
            onClick={() => {
              const newSort = opt as any;
              if (newSort === effectiveSortBy) {
                dispatch(setSortDirectionAction(effectiveSortDirection === 'asc' ? 'desc' : 'asc'));
              } else {
                dispatch(setSortByAction(newSort));
                dispatch(setSortDirectionAction(newSort === 'Position' ? 'asc' : 'desc'));
              }
              closeSortMenu();
            }}
          >
            <ListItemText primary={opt} />
          </MenuItem>
        ))}
      </Menu>
    </Stack>
  );
};

export default function Header({ onOpenNav }: Props) {
  const theme = useTheme();
  const { t } = useLocales();
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState<boolean>(false);
  const [hasUserAdjustedRange, setHasUserAdjustedRange] = useState(false);

  const filterStore = useSelector((state: any) => state.filters);

  const workspace: any = useCurrentWorkspace();
  const optionRefs: any = useRef({});

  const [compareStartDate, setCompareStartDate] = useState<any>(filterStore?.compareStart);
  const [compareEndDate, setCompareEndDate] = useState<any>(filterStore?.compareEnd);
  const [searchParams,] = useSearchParams();
  const settings = useSettingsContext();

  const extraOptions = useSelector((state: RootState) => state.filters.extraOptions);
  const router = useRouter()
  const pathname = usePathname();

  const { data: user } = useCurrentUserQuery();
  const role = user?.role;

  const tabs = getTabs(role);


  const dispatch = useDispatch()
  const displayTags = useMemo(() => {
    return ["/optimization/traffic-details", "/optimization/funnel", "/optimization/cluster-manager"].includes(location.pathname) && role !== "customer"
  }, [location.pathname])

  useEffect(() => {
    if (searchParams.has('mode') && extraOptions.mode !== searchParams.get('mode')) {
      dispatch(setExtraOptions({ ...extraOptions, mode: searchParams.get('mode') as "base" | "activations" }));
    }
  }, [searchParams, dispatch, extraOptions]);

  useEffect(() => {
    if (isFilterDrawerOpen) setIsFilterDrawerOpen(false);
  }, [pathname]);

  const isNavHorizontal = settings.themeLayout === 'horizontal';
  const isNavMini = settings.themeLayout === 'mini';
  const lgUp = useResponsive('up', 'lg');
  const offsetTop = false;

  const [start, end] = useMemo(() => {
    const startDate = new Date();
    startDate.setMonth(startDate.getUTCMonth() - 1);
    startDate.setDate(startDate.getUTCDate());
    const endDate = new Date();
    endDate.setDate(endDate.getUTCDate() - 1);
    return [startDate, endDate];
  }, []);

  const dateRangeSelect = useDateRangeSelect({
    preset: "last_30d",
    step: "year_week",
    selected: "custom",
    start,
    end
  });

  useEffect(() => {
    dispatch(setFilterDateCompare({
      compareStart: fDate(compareStartDate, "yyyy-MM-dd"),
      compareEnd: fDate(compareEndDate, "yyyy-MM-dd"),
      skipAlignment: true
    }))
  }, [compareStartDate, compareEndDate, dispatch])

  const updateFilterDate = (startDate: Date | null, endDate: Date | null, skipAlignment = true) => {
    if (startDate && endDate) {
      setHasUserAdjustedRange(true);
      const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const { start, end } = getDateRangeIso(startDate, endDate, userTimeZone);

      dispatch(setFilterDate({ start, end, skipAlignment }));

      // Calculer et mettre à jour la granularité
      const daysDifference = differenceInDays(endDate, startDate);
      let newGranularity = 'date_day';
      if (daysDifference > 365) {
        newGranularity = 'year_month';
      } else if (daysDifference > 90) {
        newGranularity = 'year_week';
      }
      dispatch(setFilterTimestep({ timestep: newGranularity, skipAlignment }));
    }
  };

  // Routes that manage their own default date range - Header should not overwrite
  const routesWithOwnDateDefaults = ['/non-branded/search-terms'];

  useEffect(() => {
    // Toujours utiliser le fuseau local de l'utilisateur
    const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (filterStore?.datesForcedByActivation) return;
    if (routesWithOwnDateDefaults.includes(location?.pathname)) return;

    if (lgUp && !workspace?.name.includes('Demo')) {
      if (dateRangeSelect?.start && dateRangeSelect?.end) {
        // Range utilisateur standard
        const { start, end } = getDateRangeIso(dateRangeSelect.start, dateRangeSelect.end, userTimeZone);
        if (filterStore?.start !== filterStore?.end) {
          const skipAlignment = hasUserAdjustedRange;
          dispatch(setFilterDate({ start, end, skipAlignment }));
        }
      }
    } else {
      if (lgUp) {
        if (dateRangeSelect?.start && dateRangeSelect?.end && filterStore.timestep === "date_day") {
          const newStartDate = new Date(2024, 8, 1); // 1er sept 2024
          const newEndDate = new Date(2024, 10, 30); // 30 nov 2024

          const { start, end } = getDateRangeIso(newStartDate, newEndDate, userTimeZone);

          dispatch(setFilterDate({ start, end }));
        } else {
          const newStartDate = new Date(2024, 8, 1); // 1er septembre 2024
          const newEndDate = new Date(2024, 10, 30); // 30 novembre 2024
          const { start, end } = getDateRangeIso(newStartDate, newEndDate, userTimeZone);

          dispatch(setFilterDate({ start, end }));
        }
      }
    }
  }, [dateRangeSelect]);

  const handleDeviceFilterChange = (selectedOptions: any) => {
    let updatedOptions;

    if (Array.isArray(selectedOptions)) {
      updatedOptions = selectedOptions
        .filter((option: string) => option !== t('dashboard.filters.all_devices'))
        .map((option: string) => option.toUpperCase());
    } else {
      updatedOptions =
        selectedOptions !== t('dashboard.filters.all_devices')
          ? [selectedOptions.toUpperCase()]
          : [];
    }

    dispatch(setFilterDevice(updatedOptions));
  };


  const handleCountryFilterChange = (selectedOptions: any) => {
    let updatedOptions;
    if (Array.isArray(selectedOptions)) {
      updatedOptions = selectedOptions.filter(
        (option: string) => option !== t('dashboard.filters.all_countries')
      );
    } else {
      updatedOptions = selectedOptions !== t('dashboard.filters.all_countries')
        ? [selectedOptions]
        : [];
    }

    if (location?.pathname === "/brand-wayback") {
      dispatch(setFilterKeyword([]))
    }

    dispatch(setFilterCountry(updatedOptions));


  };

  const handleKeywordFilterChange = (value: any) => {
    if (filterStore?.keywords) {
      if (filterStore?.keywords.includes(value)) {
        dispatch(setFilterKeyword(filterStore?.keywords.filter((keyword: any) => keyword !== value)))
      } else {
        const updatedKeywords = [...filterStore.keywords, value];
        dispatch(setFilterKeyword(updatedKeywords))
      }
    }
  };

  useEffect(() => {
    if (routesWithOwnDateDefaults.includes(location?.pathname)) {
      return; // Let the page component set its own dates (e.g. last 12 weeks)
    }

    const newStartDate = new Date();
    newStartDate.setMonth(newStartDate.getUTCMonth() - 1);
    newStartDate.setDate(newStartDate.getUTCDate());

    const newEndDate = new Date();
    newEndDate.setDate(newEndDate.getUTCDate() - 1);

    dateRangeSelect.onChangeStartDate(newStartDate);
    dateRangeSelect.onChangeEndDate(newEndDate);

    const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const { start, end } = getDateRangeIso(newStartDate, newEndDate, userTimeZone);

    dispatch(setFilterDate({ start, end }));

    dispatch(setFilterDateCompare({
      compareStart: subDays(new Date(), 60).toISOString(),
      compareEnd: subDays(new Date(), 31).toISOString(),
    }))
  }, [location?.pathname])

  const onChangePreset = (value: string) => {
    dateRangeSelect.onChangePreset(value);
    setHasUserAdjustedRange(false);
  };

  const onChangeTimeStep = (value: string) => {
    dispatch(setFilterTimestep(value));
  }

  const iconDevices: any = [
    { name: "Tablet", icon: "icon-park:ipad" },
    { name: "Mobile", icon: "icon-park:iphone" },
    { name: "Desktop", icon: "icon-park:i-mac" }
  ]

  const handleOnlyFilter = (option: string) => {
    dispatch(setFilterCountry([option]));
  };

  const savingsFormulaOptions = [
    { label: "Savings: Average", value: "m1" },
    { label: "Savings: Month over Month", value: "y1" }
  ];

  const savingsFormulaFilter = savingsFormulaOptions.find(
    (opt) => opt.value === filterStore.savingsFormulaFilter
  );

  useEffect(() => {
    if (workspace?.name.includes('Demo')) {
      const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

      const newStartDate = new Date(2024, 8, 1); // 1er septembre 2024
      const newEndDate = new Date(2024, 10, 30); // 30 novembre 2024

      const { start, end } = getDateRangeIso(newStartDate, newEndDate, userTimeZone);

      dispatch(setFilterDate({ start, end }));
      dispatch(setFilterTimestep("date_day"))
    }
  }, [workspace, location?.pathname])


  const { data: platforms, isLoading: isLoadingPlatforms } = usePlatformsQuery(
    {
      workspace: workspace?.bqTable,
    },
    { skip: !workspace?.bqTable }
  );

  const shouldSkip = !workspace?.bqTable;

  const { data: countries, isLoading: isLoadingCountries } = useCountriesQuery(
    {
      workspace: workspace?.bqTable,
      platforms: filterStore?.platforms,
      startDate: filterStore?.start,
      endDate: filterStore?.end
    },
    { skip: shouldSkip }
  );

  const { data: searchTerms } = useSearchTermsQuery(
    {
      workspace: workspace?.bqTable,
      platforms: filterStore?.platforms,
      devices: filterStore?.devices,
      startDate: filterStore?.start,
      countries: filterStore?.countries,
      endDate: filterStore?.end
    },
    { skip: shouldSkip }
  );

  const { data: searchTermsByTags } = useGetSearchTermsByTagsQuery(
    {
      workspaceId: workspace?.id as number,
      tags: filterStore?.tags.map((tag: any) => tag.label),
    },
    { skip: !workspace?.id || filterStore?.tags.length === 0 }
  );

  const { data: { tags } = {} } = useGetTagsListQuery(
    {
      workspaceId: workspace?.id as number,
    },
    { skip: !workspace?.id }
  );

  // FILTER MIN DATE
  useEffect(() => {
    const currentYear = new Date().getFullYear();
    const minDate = `${currentYear - 2}-01-01`;

    dispatch(setFilterMinDatePicker(minDate));
  }, [location?.pathname, workspace])

  // FILTER COUNTRIES
  useEffect(() => {
    if (countries?.countries?.length > 0) {
      dispatch(setFilterLoadedCountry(countries.countries));
    }
  }, [countries?.countries, workspace, location?.pathname]);

  // FILTER PLATFORMS
  useEffect(() => {
    if (platforms?.platforms?.length > 0) {
      dispatch(setFilterLoadedPlatform(platforms.platforms));
    }
  }, [platforms?.platforms, workspace]);

  // FILTER KEYWORDS
  useEffect(() => {
    if (searchTerms?.search_terms?.length > 0) {
      const hasTags = filterStore.tags.length > 0
      const hasSearchTerms = searchTermsByTags && searchTermsByTags?.search_terms && searchTermsByTags?.search_terms?.length > 0
      if (displayTags && hasTags && hasSearchTerms) {
        dispatch(setFilterLoadedKeyword(
          (searchTerms.search_terms as any[]).filter((term: any) => searchTermsByTags?.search_terms.map((st) => st.name).includes(term.search_term))))
      } else {
        dispatch(setFilterLoadedKeyword(searchTerms?.search_terms))
      }
    }
  }, [searchTerms?.search_terms, workspace, searchTermsByTags, displayTags]);

  // FILTER DEVICES
  useEffect(() => {
    dispatch(setFilterLoadedDevice(['Mobile', 'Desktop', 'Tablet']))
  }, [])

  // FILTER TAGS
  useEffect(() => {
    if (tags) {
      dispatch(setFilterLoadedTags(tags?.map((tag: any) => ({ id: tag.id, label: tag.name }))))
    }
  }, [tags, workspace]);

  const renderContent = (
    <>
      {lgUp && isNavHorizontal && <Logo sx={{ mr: 2.5 }} />}

      {!lgUp && (
        <IconButton onClick={onOpenNav}>
          <SvgColor src="/assets/icons/navbar/ic_menu_item.svg" />
        </IconButton>
      )}

      {!lgUp &&
        <FiltersButton />
      }

      {(lgUp) &&
        <>

          {!window.location.pathname.startsWith("/workspaces/") && (filterStore?.minDatePicker) &&
            location?.pathname !== "/brand-overview" &&
            location?.pathname !== "/" &&
            location?.pathname !== "/admin/users" &&
            location?.pathname !== "/optimization/campaigns" &&
            location?.pathname !== "/account" &&
            location?.pathname !== "/workspaces" &&
            location?.pathname !== "/admin/sso/list" &&
            location?.pathname !== "/brand-pulse/competitors-selection" &&
            location?.pathname !== "/brand-pulse/competitors" &&
            location?.pathname !== "/404" &&
            !location?.pathname.startsWith("/optimization/activations") &&
            <Stack width={"100%"}>
              <Box>
                <Stack sx={{ height: 54, display: 'flex' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {/* POUR JULIEN: Countries filter NE PAS INCLURE /non-branded/search-terms */}
                    {!["/non-branded/search-terms", "/non-branded/competitors", "/competitive-intelligence/overview", "/competitive-intelligence/ads", "/competitive-intelligence/st-analysis", "/competitive-intelligence/competitive-landscape"].includes(location.pathname) && (
                      <>
                        {(filterStore?.loadedCountries?.length >= 2) &&
                          <Autocomplete
                            multiple={true}
                            disableClearable={false}
                            size="small"
                            limitTags={1}
                            disableCloseOnSelect
                            options={filterStore?.loadedCountries}
                            getOptionLabel={(option: any) => option}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                sx={{ minWidth: 240, maxWidth: 300 }}
                              />
                            )}
                            value={
                              filterStore?.countries?.length === 0
                                ? [t('dashboard.filters.all_countries')]
                                : filterStore?.countries
                            }
                            renderOption={(props, option) => {
                              const country = countriesJson?.find((c) => c.name === option);
                              const flagIcon = country ? `circle-flags:${country.code}` : null;

                              return (
                                <li {...props} ref={(el) => (optionRefs.current[option] = el)}
                                  style={{ whiteSpace: 'nowrap' }} key={option} onMouseEnter={() => {
                                    optionRefs.current[option].classList.add('hovered');
                                  }}
                                  onMouseLeave={() => {
                                    optionRefs.current[option].classList.remove('hovered');
                                  }}>
                                  {option !== t('dashboard.filters.all') && (
                                    <Box
                                      display="flex"
                                      justifyContent="space-between"
                                      alignItems="center"
                                      width="100%"
                                      sx={{
                                        '&:hover .only-button': {
                                          display: 'flex',
                                        },
                                      }}
                                    >
                                      <Box>
                                        {location.pathname !== "/brand-wayback" &&
                                          <Checkbox
                                            checked={filterStore?.countries.includes(option)}
                                          />
                                        }

                                        {flagIcon ? (
                                          <Iconify icon={flagIcon} width={16} sx={{ mr: 1, position: 'relative', top: 4 }} />
                                        ) : (
                                          <img style={{ width: 14, height: 14, marginRight: 6, position: 'relative', top: -6 }} src="/assets/location.png" alt="flag" />
                                        )}
                                        <span style={{ position: 'relative', top: 2 }}>{option}</span>
                                      </Box>
                                      {location.pathname !== "/brand-wayback" &&
                                        <Button
                                          className="hover-only-button"
                                          onClick={(event) => {
                                            event.stopPropagation();
                                            handleOnlyFilter(option);
                                          }}
                                          variant="contained"
                                          color="inherit"
                                          size="small"
                                          sx={{
                                            mt: 0.3,
                                            height: 24,
                                            minWidth: 50,
                                            padding: 0,
                                            display: 'none',
                                            justifyContent: 'flex-end',
                                          }}
                                        >
                                          Only
                                        </Button>
                                      }
                                    </Box>
                                  )}
                                  {option === t('dashboard.filters.all_countries') && option}
                                </li>
                              );
                            }}
                            onChange={(event, selectedOptions) => handleCountryFilterChange(selectedOptions)}
                            renderTags={(selected, getTagProps) => {
                              const visibleTags = selected.slice(0, 1); // n’en afficher qu’un
                              const hiddenCount = selected.length - visibleTags.length;

                              return (
                                <>
                                  {visibleTags.map((option, index) => {
                                    const country = countriesJson?.find((c) => c.name === option);
                                    const flagCode = country ? country.code : null;

                                    return (
                                      <Chip
                                        {...getTagProps({ index })}
                                        key={option}
                                        icon={
                                          option !== t('dashboard.filters.all_countries') ? (
                                            flagCode ? (
                                              <FlagIcon
                                                key={flagCode}
                                                code={flagCode}
                                                sx={{ width: 16, height: 16, borderRadius: '50%' }}
                                              />
                                            ) : (
                                              <img style={{ width: 14, height: 14, marginRight: 1 }} src="/assets/location.png" alt="flag" />
                                            )
                                          ) : undefined
                                        }
                                        label={option}
                                        sx={{ backgroundColor: 'rgba(145, 158, 171, 0.32)', color: "white" }}
                                        size="small"
                                        deleteIcon={option === t('dashboard.filters.all_countries') ? <></> : undefined}
                                        variant="soft"
                                      />
                                    );
                                  })}

                                  {hiddenCount > 0 && (
                                    <Chip
                                      label={`+${hiddenCount}`}
                                      size="small"
                                      variant="outlined"
                                    />
                                  )}
                                </>
                              );
                            }}

                          />
                        }
                      </>
                    )}

                    {filterStore?.loadedPlatform?.length >= 2 &&
                      <Autocomplete
                        multiple={true}
                        limitTags={1}
                        disableClearable={false}
                        size="small"
                        options={filterStore?.loadedPlatform}
                        getOptionLabel={(option: any) => option}
                        renderInput={(params) => (<TextField {...params} />)}
                        value={filterStore?.platforms?.length === 0 ? [t('dashboard.filters.all_platforms')] : filterStore?.platforms}
                        renderOption={(props, option) => (
                          <li {...props} key={option}>
                            {option === 'Google' || option === 'Bing' ? (
                              <Iconify
                                icon={option === 'Google' ? 'devicon:google' : 'logos:bing'}
                                sx={{ width: 16, height: 16, mr: 1 }}
                              />
                            ) : null}
                            {option}
                          </li>
                        )}
                        onChange={(event, selectedOptions) => {
                          let updatedOptions;

                          if (Array.isArray(selectedOptions)) {
                            updatedOptions = selectedOptions.filter(
                              (option: string) => option !== t('dashboard.filters.all_platforms')
                            );
                          } else {
                            updatedOptions = selectedOptions !== t('dashboard.filters.all_platforms') ? [selectedOptions] : [];
                          }

                          dispatch(setFilterPlatform(updatedOptions));
                        }}
                        renderTags={(selected, getTagProps) =>
                          selected.map((option, index) => (
                            <Chip
                              {...getTagProps({ index })}
                              key={option}
                              label={option}
                              icon={
                                option === 'Google' || option === 'Bing' ? (
                                  <Iconify
                                    icon={option === 'Google' ? 'devicon:google' : 'logos:bing'}
                                    sx={{ width: 16, height: 16 }}
                                  />
                                ) : undefined
                              }
                              size="small"
                              sx={{ backgroundColor: 'rgba(145, 158, 171, 0.32)', color: "white" }}
                              deleteIcon={option === t('dashboard.filters.all_platforms') ? <></> : undefined}
                              variant="soft"
                            />
                          ))
                        }
                      />
                    }

                    {!["/non-branded/search-terms", "/text-ads", "/product-listing-ads", "/non-branded/competitors"].includes(location.pathname) && (

                      <Autocomplete
                        sx={{
                          width: 180,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          "& .MuiAutocomplete-inputRoot": {
                            flexWrap: "nowrap",
                          },
                        }}
                        multiple={true}
                        size="small"
                        options={filterStore?.loadedDevices}
                        getOptionLabel={(option: any) => option}
                        renderInput={(params) => (
                          <TextField {...params} />
                        )}
                        value={
                          filterStore?.devices?.length === 0
                            ? [t('dashboard.filters.all_devices')]
                            : filterStore?.devices
                        }
                        renderOption={(props, option) => (
                          <li {...props} key={option}>
                            <Iconify
                              icon={
                                iconDevices.find(
                                  (device: any) =>
                                    device.name.toLowerCase() === option.toLowerCase()
                                )?.icon
                              }
                              sx={{ width: 16, height: 16, mr: 1 }}
                            />
                            {option.charAt(0).toUpperCase() + option.slice(1).toLowerCase()}
                          </li>
                        )}
                        onChange={(event, selectedOptions) => handleDeviceFilterChange(selectedOptions)}
                        renderTags={(value, getTagProps) => {
                          const MAX_TAGS = 1;
                          const ALL = t('dashboard.filters.all_devices');

                          // 1) On retire le placeholder du calcul
                          const clean = (value || []).filter((v) => v !== ALL);

                          // 2) Si rien de sélectionné -> on affiche juste le placeholder (pas de +N)
                          if (clean.length === 0) {
                            return (
                              <Chip
                                key="all-devices"
                                label={ALL}
                                size="small"
                                variant="soft"
                                sx={{ backgroundColor: 'rgba(145, 158, 171, 0.32)', color: "white" }}

                              />
                            );
                          }

                          // 3) Calcul "shown / hidden" uniquement sur les vraies valeurs
                          const shown = clean.slice(0, MAX_TAGS);
                          const hidden = clean.length - shown.length;

                          return [
                            ...shown.map((option, index) => (
                              <Chip
                                {...getTagProps({ index })}
                                key={option}
                                label={option.charAt(0).toUpperCase() + option.slice(1).toLowerCase()}
                                icon={
                                  <Iconify
                                    icon={iconDevices.find((d: any) => d.name.toLowerCase() === option.toLowerCase())?.icon}
                                    sx={{ width: 16, height: 16 }}
                                  />
                                }
                                sx={{ backgroundColor: 'rgba(145, 158, 171, 0.32)', color: "white" }}
                                size="small"
                                variant="soft"
                              />
                            )),
                            hidden >= 1 ? ( // <--- pas de +N quand il n'y a qu’un seul élément
                              <Chip key="more" label={`+${hidden}`} size="small" variant="outlined" sx={{ backgroundColor: 'rgba(145, 158, 171, 0.32)', color: "white" }} />
                            ) : null,
                          ];
                        }}
                      />
                    )}
                  </Box>
                </Stack>
              </Box>
            </Stack>
          }
        </>
      }

      <Stack
        flexGrow={1}
        display={"flex"}
        height={52}
        direction="row"
        justifyContent="flex-end"
        spacing={{ xs: 0.5, sm: 1 }}
      >
        <Box sx={{ height: "100%" }} display={"flex"} flexDirection={"row"}>
          {filterStore.minDatePicker &&
            <>
              {!["/brand-pulse/competitors", "/brand-pulse/competitors-selection", "/404", "/admin/sso/list", "/", "/admin/features/live-monitoring/add", "/admin/features/live-monitoring", "/workspaces", "/workspaces/add", "/admin/users", "/admin/users/add"].includes(location.pathname) &&
                <>
                  {(lgUp) && (
                    <DateRangeSelect
                      startDate={
                        routesWithOwnDateDefaults.includes(location.pathname) && filterStore?.start
                          ? new Date(filterStore.start)
                          : (dateRangeSelect.start || new Date())
                      }
                      endDate={
                        routesWithOwnDateDefaults.includes(location.pathname) && filterStore?.end
                          ? new Date(filterStore.end)
                          : (dateRangeSelect.end || new Date())
                      }
                      compareStartDate={undefined}
                      compareEndDate={undefined}
                      selected={
                        dateRangeSelect.selected === "custom"
                          ? `${fDate(filterStore.start)} - ${fDate(filterStore.end)}`
                          : (dateRangeSelect.preset as any).displayName
                      }
                      onChangeEndDate={(endDate: Date | null) => {
                        dateRangeSelect.onChangeEndDate(endDate);
                        updateFilterDate(filterStore?.start, endDate);
                      }}
                      onChangeStartDate={(startDate: Date | null) => {
                        dateRangeSelect.onChangeStartDate(startDate);
                        updateFilterDate(startDate, filterStore?.end);
                      }}
                      onChangeCompareStartDate={(it: any) => setCompareStartDate(it)}
                      onChangeCompareEndDate={(it: any) => setCompareEndDate(it)}
                      onChangePreset={onChangePreset}
                      onChangeTimeStep={onChangeTimeStep}
                      preset={"last_30d"}
                      features={{ time_step: true }}
                      timeStep={dateRangeSelect.step as any}
                    />
                  )}
                </>
              }
            </>
          }

          <Box sx={{ ml: 1 }}>
            <NotificationsPopover />
          </Box>

          <SettingsPanel open={isFilterDrawerOpen} onClose={() => setIsFilterDrawerOpen(false)} />

          <AccountPopover />
        </Box>
      </Stack>
    </>

  );

  const headerHeight =
    lgUp
      ? (isNavHorizontal
        ? HEADER.H_DESKTOP_OFFSET
        : (offsetTop ? HEADER.H_DESKTOP_OFFSET : HEADER.H_DESKTOP))
      : HEADER.H_MOBILE;

  const matchLocation = (key: string) => {
    if (key.startsWith("regexp:")) {
      return new RegExp(key.replace("regexp:", "")).test(location.pathname);
    }
    return location.pathname.startsWith(key);
  }

  const getLocationParams = (key: string) => {
    if (key.startsWith("regexp:")) {
      const regex = new RegExp(key.replace("regexp:", ""));
      const match = regex.exec(location.pathname);
      return match?.groups || {};
    }
  }


  // Trouver le bon set de tabs
  const [currentPath, currentTabs] = Object.entries(tabs)
    .sort(([a,], [b,]) => b.length - a.length)
    .find(([key, value]) => matchLocation(key)) || ["", []] as any;


  const currentParams = getLocationParams(currentPath);

  const currentTitle = currentTabs.find((tab: any) => tab.path === location.pathname)?.title || currentTabs[0]?.title || "";

  return (
    <AppBar
      sx={{
        height: HEADER.H_MOBILE,
        zIndex: theme.zIndex.appBar + 1,

        ...bgBlur({
          color: theme.palette.background.default,
        }),
        transition: theme.transitions.create(['height'], {
          duration: theme.transitions.duration.shorter,
        }),
        ...(lgUp && {
          width: `calc(100% - ${NAV.W_VERTICAL}px)`,
          height: HEADER.H_DESKTOP,
          ...(isNavHorizontal && {
            width: 1,
            bgcolor: 'background.default',
            height: HEADER.H_DESKTOP_OFFSET,
          }),
          ...(isNavMini && {
            width: `calc(100% - ${NAV.W_MINI + 1}px)`,
          }),
        }),
        backgroundColor: "#17191a"
      }}
    >

      <Toolbar
        sx={{
          height: 1,
          px: { lg: 5 },
        }}
      >
        {!["/optimization/activations/active-mode", "/optimization/activations/ko-bidding", "/optimization/activations/ko-bidding-auto"].includes(location.pathname) &&
          <>
            {renderContent}
          </>
        }
      </Toolbar>

      <Box
        sx={{
          height: 52,
          width: "100%",
          position: "relative",
          "&::after": {
            content: '""',
            position: "absolute",
            bottom: 0,
            left: -.4,
            top: -12.5,
            width: '100%',
            backgroundColor: "white",
            height: 12,
            borderTop: (t) => `1.5px solid ${t.palette.divider}`,
            borderLeft: (t) => `1.5px solid ${t.palette.divider}`,
            borderTopLeftRadius: 12,
          },
        }}
      />

      {currentPath &&
        <Stack
          width="100%"
          mt={.1}
          sx={{
            position: 'fixed',
            top: headerHeight,
            zIndex: theme.zIndex.appBar,
            background: "white",
            borderTopLeftRadius: 12,
            borderBottom: (t) => `1.5px solid ${t.palette.divider}`,
          }}
        >
          <Box
            px={5}
            height={52}
            display="flex"
            flexDirection="row"
            alignItems="center"
          >
            <Typography mr={4} fontSize={19} fontWeight={700}>{currentTitle}</Typography>

            {

              currentTabs.filter((tab: any) => !tab.type || tab.type !== "root").map((tab: any) =>
                tab.dropdown ? (
                  <KoBiddingDropdown key={tab.path} router={router} location={location} />
                ) : (
                  <TabButton
                    sx={{ mr: 1 }}
                    key={tab.path}
                    className={location.pathname === (typeof tab.path === "function" ? tab.path(currentParams) : tab.path) ? "active" : ""}
                    onClick={() => {
                      router.push(typeof tab.path === "function" ? tab.path(currentParams) : tab.path)
                    }}
                  >
                    {tab.label}
                  </TabButton>
                )
              )}
          </Box>

          {!["/brand-pulse/competitors-selection", "/brand-pulse/competitors", "/404", "/admin/sso/list", "/account", "/non-branded/competitors", "/non-branded/search-terms", "/", "/workspaces", "/competitive-intelligence/competition-evolution", "/competitive-intelligence/st-analysis", "/competitive-intelligence/overview", "/optimization/activations/overview", "/brand-overview", "/admin/features/live-bidding/ad-group-based", "/brand-wayback", "/admin/users", "/admin/users/add", "/optimization/campaigns", "/optimization/scraping", "/optimization/activations/ko-bidding/target-impression-share", "/optimization/activations/ko-bidding/tcpa", "/optimization/activations/ko-bidding/troas", "/optimization/activations/ko-bidding/max-cpc", "/optimization/activations/active-mode", "/optimization/activations/ko-bidding-auto"].includes(location.pathname) && !window.location.pathname.startsWith("/workspaces/") && (
            <Stack
              sx={{
                height: 52,
                backgroundColor: theme.palette.mode === "dark" ? "#2f363f" : 'white',
                px: 5,
                borderTop: "1px dashed rgba(145, 158, 171, 0.2)",
                display: "flex",
              }}
            >
              <Box
                height={"100%"}
                display="flex"
                flexDirection="row"
                alignItems="center"
              >

                {!["/competitive-intelligence/ads", "/competitive-intelligence/st-analysis", "/competitive-intelligence/competition-evolution", "/brand-overview", "/optimization/overview", "/brand-wayback", "/conversions-analysis"].includes(location.pathname) && (
                  <Box mr={1}>
                    <InfiniteScrollKeywords
                      keywords={(filterStore?.loadedKeyword || [])}
                      onKeywordChange={(it: any) => handleKeywordFilterChange(it)}
                    />
                  </Box>
                )}

                {location.pathname === "/competitive-intelligence/ads" && (
                  <CompetitiveIntelligenceAdsHeader />
                )}

                {displayTags
                  && filterStore.loadedTags.length > 0 && (
                    <Box>
                      <GenericInfiniteScrollList
                        limitTags={2}
                        sx={{ width: 275 }}
                        fullWidth={true}
                        searchPlaceholder=""
                        items={(filterStore as RootState["filters"]).loadedTags}
                        options={(filterStore as RootState["filters"]).loadedTags}
                        multiple={true}
                        value={
                          filterStore.tags.length === 0
                            ? [{ id: "__default__", label: "Clusters" }]
                            : filterStore.tags
                        }
                        onSelectionChange={(selected) => {
                          const cleaned = selected.filter((item: any) => item.id !== "__default__");
                          dispatch(
                            setFilterTags(
                              cleaned.map((selectedTag: any) =>
                                filterStore.loadedTags.find(
                                  (tag: any) => tag.id === (selectedTag.id || selectedTag)
                                )
                              )
                            )
                          );
                        }}
                        getOptionLabel={(item: any) => item?.label || (item as any)}
                        getOptionKey={(item: any) => item.id || (item as any)}
                        isOptionEqualToValue={(item, value) => (item.id || item) === (value.id || value)}
                        customItemRenderer={(item) => (
                          <Chip
                            label={item.label || (item as any)}
                            size="small"
                            variant="soft"
                            sx={{
                              backgroundColor: item?.label
                                ? generateColorFromString(String(item.label))
                                : undefined,
                              color: "#fff"
                            }}
                          />
                        )}
                        customTagRenderer={(item) => {
                          const isDefault = item.id === "__default__";

                          return (
                            <Chip
                              label={item.label}
                              size="small"
                              variant={isDefault ? "soft" : "soft"} // style différent
                              sx={{
                                backgroundColor: isDefault
                                  ? "rgba(220, 224, 228)" // gris clair pour la chip "test"
                                  : generateColorFromString(item.label),
                                color: isDefault ? "rgba(33, 43, 54)" : "#fff", // texte gris pour la chip "test"
                                pointerEvents: isDefault ? "none" : "auto" // non cliquable si c’est la chip test
                              }}
                              onDelete={
                                isDefault
                                  ? undefined
                                  : () => {
                                    dispatch(
                                      setFilterTags(
                                        filterStore.tags.filter(
                                          (tag: any) => tag.id !== item.id
                                        ) as any
                                      )
                                    );
                                  }
                              }
                            />
                          );
                        }}
                      />


                    </Box>
                  )}

                {["/optimization/overview"].includes(location.pathname) && (

                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "flex-end",
                      alignItems: "center",
                      width: "100%",
                    }}
                  >
                    <Autocomplete
                      options={savingsFormulaOptions}
                      value={savingsFormulaFilter}
                      disableClearable
                      onChange={(_, newValue) => {
                        dispatch(setSavingsFormulaFilter(newValue?.value || "m1"));
                      }}
                      sx={{ width: 200 }}
                      renderInput={(params) => (
                        <TextField {...params} size="small" />
                      )}
                    />
                  </Box>
                )}
              </Box>
            </Stack>
          )}
        </Stack>
      }
    </AppBar>
  );
}
